/**
 * VERSION B — "ARCHIVE TABLE"
 *
 * The question this version asks: **what if nothing is illustrated, and everything is EVIDENCE?**
 *
 * Every plate is a top-down photograph of real objects on a dark cloth, shot rostrum-style, and
 * the whole 45s is one investigator working across that table. Density comes the way Vox actually
 * gets it (research 01 §C1): the PLATE is held 5-6s and annotation events are stacked on it every
 * 0.5-1.5s — circle-scribble, highlight sweep, drawn arrow, index card, label.
 *
 * Rostrum law (research 04 §7): ONE axis per beat · always scale DOWN from ~1.35× · never fully
 * still · the light is baked into the plate, so the camera may not contradict it.
 *
 * ⛔ No illustration, no vector figures, no panorama. If it is on screen, it is a thing.
 * Register: forensic. Bone paper, charcoal cloth, ONE marker yellow + ONE red pencil.
 * Type: Sarpanch 900 (headline) / Khand 600 (labels) / Tiro Devanagari (the quoted term).
 */
import React from "react";
import { AbsoluteFill, Audio, Img, staticFile, Easing, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { loadFont as Sarpanch } from "@remotion/google-fonts/Sarpanch";
import { loadFont as Khand } from "@remotion/google-fonts/Khand";
import { loadFont as Tiro } from "@remotion/google-fonts/TiroDevanagariSanskrit";
import {
  Plate,
  Punch,
  Label,
  Highlight,
  Scribble,
  DrawLine,
  Grain,
  Vignette,
  useKeys,
  useSpringAt,
  devaBlock,
} from "./kit45";

const head = Sarpanch("normal", { weights: ["900", "700"], subsets: ["devanagari", "latin"] }).fontFamily;
const lab = Khand("normal", { weights: ["600", "700"], subsets: ["devanagari", "latin"] }).fontFamily;
const verse = Tiro("normal", { weights: ["400"], subsets: ["devanagari"] }).fontFamily;

const MARKER = "#F2C641";
const PENCIL = "#C0392B";
const sf = (p: string) => staticFile(p);

/**
 * Rostrum — a copy-stand camera over a flat plate. `moves` is [second, x%, y%, scale].
 * Percentages are of the frame, so a move reads the same at any render scale.
 */
const Rostrum: React.FC<{
  src: string;
  moves: [number, number, number, number][];
  children?: React.ReactNode;
}> = ({ src, moves, children }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const secs = moves.map((m) => Math.round(m[0] * fps));
  const pick = (i: 1 | 2 | 3) =>
    interpolate(frame, secs, moves.map((m) => m[i]), {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.bezier(0.5, 0, 0.25, 1),
    });
  const s = pick(3);
  /**
   * ⛔ CLAMP. `scale(s) translate(x)` displaces the plate by s·x on screen, and the plate only
   * has (s-1)·W of slack in total. Asking for more pans straight off the edge into black —
   * which is exactly what a rostrum camera can never do. Max |x| = (s-1)·W / (2s).
   */
  const maxX = ((s - 1) * width) / (2 * s);
  const maxY = ((s - 1) * height) / (2 * s);
  const clamp = (v: number, m: number) => Math.max(-m, Math.min(m, v));
  const x = clamp((pick(1) / 100) * width, maxX);
  const y = clamp((pick(2) / 100) * height, maxY);
  return (
    <AbsoluteFill style={{ overflow: "hidden", backgroundColor: "#0B0B0C" }}>
      <AbsoluteFill
        style={{
          transform: `scale(${s.toFixed(4)}) translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`,
          transformOrigin: "50% 50%",
        }}
      >
        <Img src={src} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        {children}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/** A paper card laid onto the table — hard shadow + white cut edge is what reads as PASTED. */
const Card: React.FC<{
  at: number;
  x: number;
  y: number;
  w: number;
  rot?: number;
  children: React.ReactNode;
  out?: number;
}> = ({ at, x, y, w, rot = -1.2, children, out }) => {
  const s = useSpringAt(at, { damping: 17, stiffness: 210, mass: 0.7 });
  const gone = out === undefined ? 1 : useKeys([[out, 1], [out + 0.22, 0]], Easing.in(Easing.quad));
  return (
    <div
      data-audit="card"
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        opacity: gone,
        transform: `rotate(${rot}deg) scale(${(0.94 + s * 0.06).toFixed(3)}) translateY(${((1 - s) * 14).toFixed(1)}px)`,
        background: "#EFE7D6",
        boxShadow: "0 18px 26px rgba(0,0,0,0.55), 0 2px 0 rgba(255,255,255,0.35)",
        padding: "16px 22px 20px",
        clipPath: `inset(0 ${(100 - s * 100).toFixed(1)}% 0 0)`,
      }}
    >
      {children}
    </div>
  );
};

/* x = the object's real position in the B1 plate, measured off the generated image:
   razor+hair 13 · garland 33 · key 53 · havan wood 69 · pot 88. जन्म's swaddling cloth sits at
   the far left edge, so the camera starts panned left to bring it into frame. */
const OCC: [number, string, number][] = [
  [0.30, "जन्म", 2.5],
  [1.58, "मुंडन", 13],
  [2.40, "शादी", 33],
  [3.28, "गृह प्रवेश", 53],
  [4.26, "हवन", 69],
  [5.94, "अंतिम संस्कार", 88],
];

export const VerB_Archive: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#0B0B0C" }}>
    <Audio src={sf("sanatan/audio/gotra45.mp3")} />

    {/* ── P1 0-7 · SIX OBJECTS, ONE TRACK ─────────────────────────────────
        One axis (X), one held plate, six annotation events. The camera never stops. */}
    <Plate from={0} to={7.0} lead={0}>
      <Rostrum
        src={sf("sanatan45/B/B1_six_objects.png")}
        moves={[
          [0, 12, 1, 1.6],
          [7.0, -8, -1, 1.3],
        ]}
      >
        {/* ⛔ These annotations live INSIDE the rostrum transform, i.e. in PLATE space. If they
            sat in screen space they would slide off their own objects the moment the camera
            moved — the classic annotated-still bug. Coordinates are % of the plate, measured
            off the actual generated image. */}
        {OCC.map(([t, w, x], i) => (
          <div key={w} style={{ position: "absolute", left: `${x}%`, top: "60%", transform: "translateX(-50%)" }}>
            <Scribble at={t} w={150} h={196} color={PENCIL} strokeWidth={2.6} seed={i + 2}
              style={{ left: "50%", top: -172, transform: "translateX(-50%)" }} />
            <Punch at={t + 0.1} text={w} size={26} color={i === OCC.length - 1 ? MARKER : "#F3ECDC"} weight={900}
              style={{ fontFamily: head, textShadow: "0 4px 18px rgba(0,0,0,0.95)" }} />
          </div>
        ))}
      </Rostrum>
      <Vignette strength={0.55} />
      <Label at={0.9} text="छह मौके · एक ही सवाल" size={22} color="rgba(243,236,220,0.62)"
        style={{ position: "absolute", left: 78, top: 74, fontFamily: lab }} />
    </Plate>

    {/* ── P2 7-12.2 · THE REGISTER, AND THE QUESTION ─────────────────────── */}
    <Plate from={7.0} to={12.2}>
      <Rostrum
        src={sf("sanatan45/B/B2_ledger.png")}
        moves={[
          [7.0, -6, -10, 1.42],
          [10.4, 2, 4, 1.12],
          [12.2, 4, 7, 1.08],
        ]}
      />
      <Vignette strength={0.6} />
      {/* the highlight sweep — the single most copied Vox move, over the ledger line */}
      <div style={{ position: "absolute", left: "18%", top: "40%", width: "40%", height: 44 }}>
        <Highlight at={8.69} color={MARKER} dur={0.42} />
      </div>
      <Card at={10.69} x={168} y={366} w={620} rot={-1.4} out={12.05}>
        <div style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 66, color: "#16171A", lineHeight: 1.34 }}>
          आपका गोत्र क्या है
        </div>
      </Card>
      <Label at={7.7} text="पंडित जी · वही सवाल" size={21} color="rgba(243,236,220,0.66)"
        style={{ position: "absolute", left: 78, top: 74, fontFamily: lab }} />
    </Plate>

    {/* ── P3 12.2-15.9 · THE PHONE ───────────────────────────────────────── */}
    <Plate from={12.2} to={15.9}>
      <Rostrum
        src={sf("sanatan45/B/B3_phone.png")}
        moves={[
          [12.2, 0, 0, 1.36],
          [15.9, -3, -4, 1.16],
        ]}
      />
      <Vignette strength={0.6} />
      <Scribble at={14.79} w={330} h={330} color={PENCIL} strokeWidth={4} seed={11}
        style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} />
      <div style={{ position: "absolute", left: "50%", top: "74%", transform: "translateX(-50%)" }}>
        <Punch at={14.95} text="घर पर फ़ोन" size={46} color="#EFE7D6" weight={900} style={{ fontFamily: head }} />
      </div>
      <Label at={12.9} text="हम में से ज़्यादातर" size={21} color="rgba(230,236,244,0.6)"
        style={{ position: "absolute", left: 78, top: 74, fontFamily: lab }} />
    </Plate>

    {/* ── P4 15.9-19 · THE RESET ─────────────────────────────────────────── */}
    <Plate from={15.9} to={19.0}>
      <Rostrum
        src={sf("sanatan45/B/B4_empty_table.png")}
        moves={[
          [15.9, 4, 3, 1.3],
          [19.0, -2, -2, 1.14],
        ]}
      />
      <Vignette strength={0.5} />
      <Card at={15.95} x={150} y={196} w={700} rot={-0.9}>
        <div style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 58, color: "#16171A" }}>
          सीधे काम की बात
        </div>
        <div style={{ height: 10 }} />
        <div style={{ ...devaBlock, fontFamily: lab, fontWeight: 600, fontSize: 30, color: "#7A2B22" }}>
          गोत्र होता क्या है
        </div>
      </Card>
      <DrawLine at={17.67} d="M 170 356 L 800 356" len={630} color={PENCIL} strokeWidth={3} dur={0.5} />
    </Plate>

    {/* ── P5 19-22.7 · THREE CARDS ───────────────────────────────────────── */}
    <Plate from={19.0} to={22.7}>
      <Rostrum
        src={sf("sanatan45/B/B5_three_cards.png")}
        moves={[
          [19.0, 0, 3, 1.32],
          [22.7, 0, -3, 1.14],
        ]}
      />
      <Vignette strength={0.52} />
      {([[19.2, "गोत्र", 17], [21.25, "प्रवर", 50], [22.02, "सपिंड", 83]] as [number, string, number][]).map(
        ([t, w, x], i) => (
          <div key={w} style={{ position: "absolute", left: `${x}%`, top: "47%", transform: "translate(-50%,-50%)" }}>
            <Punch at={t} text={w} size={44} color="#1B1A18" weight={900} style={{ fontFamily: head }} />
            {i > 0 ? (
              <DrawLine at={t} d={`M -70 26 L -14 26`} len={56} color={PENCIL} strokeWidth={3} dur={0.22} />
            ) : null}
          </div>
        ),
      )}
      <Label at={20.4} text="तीन शब्द, एक ही सवाल के" size={21} color="rgba(243,236,220,0.62)"
        style={{ position: "absolute", left: 78, bottom: 78, fontFamily: lab }} />
    </Plate>

    {/* ── P6 22.7-29.9 · FOUR PACKETS ────────────────────────────────────── */}
    <Plate from={22.7} to={29.9}>
      <Rostrum
        src={sf("sanatan45/B/B6_four_folds.png")}
        moves={[
          [22.7, 9, 0, 1.46],
          [29.9, -7, 0, 1.26],
        ]}
      />
      <Vignette strength={0.55} />
      {([[22.75, "ये क्या हैं", 12], [23.66, "महत्व", 37], [25.44, "शादी में क्या", 62], [27.72, "विज्ञान", 87]] as [number, string, number][]).map(
        ([t, w, x], i) => (
          <div key={w} style={{ position: "absolute", left: `${x}%`, top: "72%", transform: "translateX(-50%)" }}>
            <Punch at={t} text={w} size={26} color="#F3ECDC" weight={700} style={{ fontFamily: lab }} />
            {i === 3 ? <Highlight at={28.32} color={MARKER} dur={0.3} /> : null}
          </div>
        ),
      )}
    </Plate>

    {/* ── P7 29.9-37.1 · THE COPPER PLATE — the thesis object ─────────────
        A ताम्रशासन is a legally binding land grant that OPENS by reciting the donor's
        genealogy. It is the reason this topic has physical evidence at all. */}
    <Plate from={29.9} to={37.1}>
      <PlateSevenB />
    </Plate>

    {/* ── P8 37.1-45.6 · THE VAṂŚĀVALĪ ───────────────────────────────────── */}
    <Plate from={37.1} to={45.6} tail={0}>
      <PlateEightB />
    </Plate>

    <Grain opacity={0.17} freq={0.9} />
  </AbsoluteFill>
);

const PlateSevenB: React.FC = () => {
  const split = useKeys([[35.32, 0], [36.05, 1]], Easing.inOut(Easing.cubic));
  return (
    <>
      <Rostrum
        src={sf("sanatan45/B/B7_copperplate.png")}
        moves={[
          [29.9, -4, -5, 1.46],
          [33.4, 0, 0, 1.22],
          [37.1, 3, 3, 1.14],
        ]}
      />
      <Vignette strength={0.58} />
      <div style={{ position: "absolute", left: 78, top: 68 }}>
        <Punch at={32.36} text="जड़" size={104} color="#F3ECDC" weight={900} out={34.9}
          style={{ fontFamily: head, textShadow: "0 8px 30px rgba(0,0,0,0.85)" }} />
      </div>
      <Label at={30.4} text="ताम्रशासन · दानपत्र" size={21} color="rgba(243,236,220,0.6)"
        style={{ position: "absolute", left: 78, bottom: 78, fontFamily: lab }} />
      {/* the split — drawn as a pencil rule, not a wipe */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "10%",
          bottom: "10%",
          width: 3,
          background: PENCIL,
          transform: `translateX(-50%) scaleY(${split.toFixed(3)})`,
          transformOrigin: "50% 0%",
        }}
      />
      <Card at={35.58} x={92} y={432} w={330} rot={-1.6}>
        <div style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 34, color: "#16171A" }}>पहली व्यवस्था</div>
      </Card>
      <Card at={35.9} x={790} y={432} w={330} rot={1.4}>
        <div style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 34, color: "#7C766A" }}>दूसरी व्यवस्था</div>
      </Card>
    </>
  );
};

const PlateEightB: React.FC = () => {
  const trace = useKeys([[43.6, 0], [45.6, 1]], Easing.out(Easing.cubic));
  const headIn = useSpringAt(39.79, { damping: 17, stiffness: 160 });
  return (
    <>
      <Rostrum
        src={sf("sanatan45/B/B8_vanshavali.png")}
        moves={[
          [37.1, -9, 5, 1.48],
          [41.6, 0, 0, 1.26],
          [45.6, 6, -4, 1.14],
        ]}
      />
      <Vignette strength={0.55} />
      {/* heading that arrives and STAYS for the section (Yash's BOXES→HEADING device) */}
      <div style={{ position: "absolute", left: 78, top: 66, opacity: headIn, transform: `translateY(${((1 - headIn) * 22).toFixed(1)}px)` }}>
        <span style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 56, color: "#F3ECDC" }}>आर्ष गोत्र</span>
        <div style={{ height: 3, background: MARKER, transform: `scaleX(${headIn.toFixed(3)})`, transformOrigin: "0% 50%", marginTop: 2 }} />
      </div>
      <div style={{ position: "absolute", left: 80, top: 158 }}>
        <span style={{ ...devaBlock, fontFamily: verse, fontSize: 30, color: "rgba(243,236,220,0.8)", opacity: headIn }}>
          <Punch at={42.03} text="ऋषि से जुड़ा हुआ" size={30} color="rgba(243,236,220,0.85)" weight={400} style={{ fontFamily: verse }} />
        </span>
      </div>
      {/* the descent line, traced UP the scroll on the two spoken पिता */}
      <DrawLine at={43.81} d="M 980 590 L 980 470 L 900 470 L 900 350" len={320} color={PENCIL} strokeWidth={4} dur={1.6} />
      <div style={{ position: "absolute", right: 96, top: 500 }}>
        <Punch at={43.81} text="पिता" size={42} color="#F3ECDC" weight={900} style={{ fontFamily: head }} />
      </div>
      <div style={{ position: "absolute", right: 176, top: 300, opacity: trace > 0.55 ? 1 : 0 }}>
        <Punch at={45.33} text="उनके पिता" size={42} color={MARKER} weight={900} style={{ fontFamily: head }} />
      </div>
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
